import React from 'react';
/**
 * DjayAvatar part — treatment finishes (the cosmetic "rarity finish" axis).
 *
 * Treatments are ORTHOGONAL to variant/accessory: they layer aura / glow /
 * gummy inner-light / sparkle on top of ANY skin without touching identity
 * (DJAY_MASCOT_DOMAIN_2026 §Treatments). Inspired by — not copied from — the
 * Fortnite.GG locker (translucent gummy body, inner glow, drifting particles).
 *
 * Renders in two passes so it can sit on both sides of the body z-stack:
 *   layer="under" → behind the body (aura halo)
 *   layer="over"  → in front of the body (inner glow, sparkle particles)
 *
 * The radial-glow gradient + soft-blur filter <defs> live in the orchestrator
 * (DjayAvatarView) keyed off `uid`, so this part only references them.
 */
import type { DjayTreatment, VariantColorScheme } from '../types';
interface TreatmentProps {
  treatment: DjayTreatment;
  layer: 'under' | 'over';
  uid: string;
  colors: VariantColorScheme;
  showDetails: boolean;
}
/** Deterministic-ish particle positions above the head (viewBox 0 0 200 220). */
const SPARKLE_BITS = [{
  cx: 100,
  cy: 16,
  r: 2.4,
  delay: '0s'
}, {
  cx: 78,
  cy: 30,
  r: 1.8,
  delay: '0.6s'
}, {
  cx: 124,
  cy: 26,
  r: 2,
  delay: '1.1s'
}, {
  cx: 64,
  cy: 46,
  r: 1.4,
  delay: '1.6s'
}, {
  cx: 138,
  cy: 44,
  r: 1.6,
  delay: '0.3s'
}, {
  cx: 110,
  cy: 8,
  r: 1.6,
  delay: '1.4s'
}];
function Diamond({
  cx,
  cy,
  r,
  fill
}: {
  cx: number;
  cy: number;
  r: number;
  fill: string;
}) {
  return <path d={`M${cx} ${cy - r} L${cx + r} ${cy} L${cx} ${cy + r} L${cx - r} ${cy} Z`} fill={fill} data-id="element-3377" data-mp="g1usgvks7alt" />;
}
export function Treatment({
  treatment,
  layer,
  uid,
  colors,
  showDetails
}: TreatmentProps) {
  if (treatment === 'none') return null;
  // Particles + gummy detail only read well at larger sizes.
  const accent = colors.treatmentAccent;
  // ── UNDER layer (behind body) ──────────────────────────────────────
  if (layer === 'under') {
    if (treatment === 'aura') {
      return <>
          {/* Slow counter-rotating energy ring sits behind the bloom for a
                          richer, layered halo. Separate <g> so it can spin independently
                          of the breathing bloom (both disabled under reduced-motion). */}
          <g className="djay-aura-ring" aria-hidden="true" data-id="element-3378" data-mp="3sm6jlydj4och">
            <circle cx="100" cy="120" r="86" fill="none" stroke={accent} strokeOpacity="0.5" strokeWidth="2.5" strokeDasharray="14 22" strokeLinecap="round" data-id="element-3379" data-mp="1d95xo6ruhw2" />
          </g>
          <g className="djay-aura" aria-hidden="true" data-id="element-3380" data-mp="fda7l5uv1tcl">
            <circle cx="100" cy="120" r="92" fill={`url(#${uid}-treatment-radial)`} data-id="element-3381" data-mp="2c6lpx0071jsf" />
            <circle cx="100" cy="120" r="68" fill={`url(#${uid}-treatment-radial)`} opacity="0.55" data-id="element-3382" data-mp="2h5f28p3qv4r0" />
            <ellipse cx="100" cy="120" rx="80" ry="86" fill="none" stroke={accent} strokeOpacity="0.45" strokeWidth="2" data-id="element-3383" data-mp="24tcrmhyzafum" />
            <ellipse cx="100" cy="120" rx="66" ry="72" fill="none" stroke={accent} strokeOpacity="0.25" strokeWidth="1.5" data-id="element-3384" data-mp="2ski8n0gnne6i" />
          </g>
        </>;
    }
    if (treatment === 'glow' || treatment === 'gummy') {
      // A subtle halo so the body reads as lit from within.
      return <g className="djay-treatment-halo" aria-hidden="true" data-id="element-3385" data-mp="177ows1i685l7">
          <circle cx="100" cy="125" r="80" fill={`url(#${uid}-treatment-radial)`} opacity={treatment === 'gummy' ? 0.9 : 0.55} data-id="element-3386" data-mp="174uae4flkap5" />
        </g>;
    }
    return null;
  }
  // ── OVER layer (in front of body) ──────────────────────────────────
  return <g aria-hidden="true" data-id="element-3387" data-mp="2h4dgqise1jop">
      {/* Gummy: translucent inner light core + glossy top sheen */}
      {treatment === 'gummy' && <g className="djay-gummy" filter={`url(#${uid}-treatment-soft)`} data-id="element-3388" data-mp="2m3yif0yy0654">
          <ellipse cx="100" cy="140" rx="40" ry="46" fill={accent} fillOpacity="0.35" data-id="element-3389" data-mp="39s9apfqkoxhs" />
          <ellipse cx="100" cy="155" rx="22" ry="26" fill="#FFFFFF" fillOpacity="0.28" data-id="element-3390" data-mp="3ephv7mz71dm7" />
          {showDetails && <path d="M70 70 C84 58, 116 58, 130 70 C116 64, 84 64, 70 70 Z" fill="#FFFFFF" fillOpacity="0.45" data-id="element-3391" data-mp="1pcxnw708n0q1" />}
        </g>}

      {/* Glow + aura: soft inner glow bloom over the body */}
      {(treatment === 'glow' || treatment === 'aura') && <g className="djay-inner-glow" filter={`url(#${uid}-treatment-soft)`} data-id="element-3392" data-mp="1gwf4om36h7ls">
          <ellipse cx="100" cy="135" rx="36" ry="42" fill={accent} fillOpacity={treatment === 'aura' ? 0.4 : 0.28} data-id="element-3393" data-mp="1vt66f20bqvek" />
        </g>}

      {/* Sparkle + aura: drifting particle bits above the head */}
      {(treatment === 'sparkle' || treatment === 'aura') && showDetails && <g className="djay-sparkles" data-id="element-3394" data-mp="y6z11pw74z15">
          {SPARKLE_BITS.map((p, i) => <g key={i} className="djay-sparkle-bit" style={{
        animationDelay: p.delay
      }} data-id="element-3395" data-mp="1cv3p79e0q2rb" data-mpi={i}>
              <Diamond cx={p.cx} cy={p.cy} r={p.r * 1.8} fill={accent} data-id="element-3396" data-mp="kdhnclq6ygj1" />
              <Diamond cx={p.cx} cy={p.cy} r={p.r} fill="#FFFFFF" data-id="element-3397" data-mp="fexxia8h8kle" />
            </g>)}
        </g>}
    </g>;
}